import type {
  IComparedFacts,
  IFactTable,
  IPeriodFacts,
} from '../types/metrics';
import type { IComponentFacts, ReportComponentId } from '../types/report';
import type { ITableModel, ITableRowModel } from '../types/tables';
import { BUCKET_LABELS } from './BucketHelper';
import { MetricsHelper } from './MetricsHelper';
import {
  BALANCE_SHEET_ROWS,
  type IStatementRowSpec,
  PNL_ROWS,
  WORKING_CAPITAL_ROWS,
} from './tableRowSpecs';

const INCOME_SECTIONS = ['revenue', 'non_operating_revenue'];

/** Builds the presentation-agnostic table models behind every table component. */
export class TableModelHelper {
  static readonly TOP_EXPENSES_LIMIT = 15;

  static readonly VARIANCE_LIMIT = 12;

  /** Movements at or above this share of the prior value are flagged in the variance table. */
  static readonly LARGE_VARIANCE_PCT = 0.2;

  /** The table model for a table component, or null for tiles and charts. */
  static forComponent(
    id: ReportComponentId,
    facts: IComponentFacts
  ): ITableModel | null {
    const label = facts.resolved.comparisonLabel;
    switch (id) {
      case 'pnl_summary_table':
        return TableModelHelper.pnlSummary(facts, label);
      case 'top_expenses_table':
        return TableModelHelper.topExpenses(facts.table, facts, label);
      case 'variance_table':
        return TableModelHelper.variance(facts.table, facts, label);
      case 'balance_sheet_table':
        return TableModelHelper.balanceSheet(facts, label);
      case 'working_capital_table':
        return TableModelHelper.workingCapital(facts, label);
      default:
        return null;
    }
  }

  static pnlSummary(
    { current, comparison }: IComparedFacts,
    comparisonLabel: string
  ): ITableModel {
    return {
      columns: [
        { key: 'label', label: '', format: 'text' },
        { key: 'current', label: 'Current', format: 'currency' },
        { key: 'comparison', label: comparisonLabel, format: 'currency' },
        { key: 'delta', label: 'Δ', format: 'delta' },
        { key: 'pct', label: 'Δ %', format: 'signedPercent' },
        { key: 'margin', label: '% rev', format: 'percent' },
      ],
      rows: PNL_ROWS.filter((r) =>
        TableModelHelper.isVisible(r, current, comparison)
      ).map((r) => {
        const cur = r.pick(current);
        const prev = comparison ? r.pick(comparison) : null;
        const margin = r.margin
          ? r.margin(current)
          : TableModelHelper.shareOfRevenue(cur, current);
        return {
          key: r.key,
          label: r.label,
          kind: r.kind ?? 'line',
          indent: r.indent,
          cells: [
            cur,
            prev,
            prev === null ? null : cur - prev,
            MetricsHelper.pctChange(cur, prev),
            margin,
          ],
        };
      }),
    };
  }

  static topExpenses(
    table: IFactTable,
    { current, comparison }: IComparedFacts,
    comparisonLabel: string
  ): ITableModel {
    const lines = MetricsHelper.expenseLines(table, current, comparison).slice(
      0,
      TableModelHelper.TOP_EXPENSES_LIMIT
    );
    return {
      columns: [
        { key: 'label', label: 'Ledger line', format: 'text' },
        { key: 'bucket', label: 'Bucket', format: 'text' },
        { key: 'current', label: 'Current', format: 'currency' },
        { key: 'comparison', label: comparisonLabel, format: 'currency' },
        { key: 'delta', label: 'Δ', format: 'delta' },
        { key: 'pct', label: 'Δ %', format: 'signedPercent' },
        { key: 'rev', label: '% rev', format: 'percent' },
      ],
      rows: lines.map((l) => ({
        key: l.line.id,
        label: l.line.name,
        cells: [
          BUCKET_LABELS[l.bucket],
          l.current,
          comparison ? l.prior : null,
          comparison ? l.delta : null,
          l.pct,
          l.pctOfRevenue,
        ],
      })),
    };
  }

  static variance(
    table: IFactTable,
    { current, comparison }: IComparedFacts,
    comparisonLabel: string
  ): ITableModel {
    const movers = comparison
      ? MetricsHelper.lineMovers(table, current, comparison).slice(
          0,
          TableModelHelper.VARIANCE_LIMIT
        )
      : [];
    return {
      columns: [
        { key: 'label', label: 'Ledger line', format: 'text' },
        { key: 'section', label: 'Section', format: 'text' },
        { key: 'current', label: 'Current', format: 'currency' },
        { key: 'comparison', label: comparisonLabel, format: 'currency' },
        { key: 'delta', label: 'Δ', format: 'delta' },
        { key: 'pct', label: 'Δ %', format: 'signedPercent' },
      ],
      rows: movers.map((m) => {
        const isIncome = INCOME_SECTIONS.includes(m.line.section);
        return {
          key: m.line.id,
          label: m.line.name,
          cells: [
            m.line.section.replaceAll('_', ' '),
            m.current,
            m.prior,
            m.delta,
            m.pct,
          ],
          meta: {
            favourable: isIncome ? m.delta > 0 : m.delta < 0,
            large:
              m.pct !== null &&
              Math.abs(m.pct) >= TableModelHelper.LARGE_VARIANCE_PCT,
          },
        };
      }),
    };
  }

  static balanceSheet(
    { current, comparison }: IComparedFacts,
    comparisonLabel: string
  ): ITableModel {
    return {
      columns: [
        { key: 'label', label: '', format: 'text' },
        { key: 'current', label: 'Closing', format: 'currency' },
        { key: 'comparison', label: comparisonLabel, format: 'currency' },
        { key: 'delta', label: 'Δ', format: 'delta' },
        { key: 'pct', label: 'Δ %', format: 'signedPercent' },
      ],
      rows: BALANCE_SHEET_ROWS.filter((r) =>
        TableModelHelper.isVisible(r, current, comparison)
      ).map<ITableRowModel>((r) => {
        if (r.kind === 'header') {
          return {
            key: r.key,
            label: r.label,
            kind: r.kind,
            cells: [null, null, null, null],
          };
        }
        const cur = r.pick(current);
        const prev = comparison ? r.pick(comparison) : null;
        return {
          key: r.key,
          label: r.label,
          kind: r.kind ?? 'line',
          indent: r.indent,
          cells: [
            cur,
            prev,
            prev === null ? null : cur - prev,
            MetricsHelper.pctChange(cur, prev),
          ],
        };
      }),
    };
  }

  static workingCapital(
    { current, comparison }: IComparedFacts,
    comparisonLabel: string
  ): ITableModel {
    return {
      columns: [
        { key: 'label', label: 'Metric', format: 'text' },
        { key: 'current', label: 'Current', format: 'days' },
        { key: 'comparison', label: comparisonLabel, format: 'days' },
        { key: 'delta', label: 'Δ days', format: 'days' },
        { key: 'basis', label: 'Basis', format: 'text' },
      ],
      rows: WORKING_CAPITAL_ROWS.map((r) => {
        const cur = r.pick(current);
        const prev = comparison ? r.pick(comparison) : null;
        return {
          key: r.key,
          label: r.label,
          kind: r.key === 'ccc' ? 'total' : 'line',
          cells: [
            cur,
            prev,
            cur !== null && prev !== null ? cur - prev : null,
            r.basis,
          ],
        };
      }),
    };
  }

  /** Structural rows always show; plain lines only when either window has a value. */
  private static isVisible(
    row: IStatementRowSpec,
    current: IPeriodFacts,
    comparison: IPeriodFacts | null
  ): boolean {
    if (row.kind) return true;
    return (
      row.pick(current) !== 0 || (!!comparison && row.pick(comparison) !== 0)
    );
  }

  private static shareOfRevenue(
    value: number,
    facts: IPeriodFacts
  ): number | null {
    return facts.pnl.revenue ? value / facts.pnl.revenue : null;
  }
}
